import React from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

const colorMap: Record<string, { iconBg: string; iconText: string }> = {
  primary: {
    iconBg: 'bg-primary-50 dark:bg-primary-900/20',
    iconText: 'text-primary-600 dark:text-primary-400',
  },
  success: {
    iconBg: 'bg-success-50 dark:bg-success-900/20',
    iconText: 'text-success-600 dark:text-success-400',
  },
  warning: {
    iconBg: 'bg-warning-50 dark:bg-warning-900/20',
    iconText: 'text-warning-600 dark:text-warning-400',
  },
  error: {
    iconBg: 'bg-error-50 dark:bg-error-900/20',
    iconText: 'text-error-600 dark:text-error-400',
  },
  info: {
    iconBg: 'bg-info-50 dark:bg-info-900/20',
    iconText: 'text-info-600 dark:text-info-400',
  },
};

interface WidgetShellProps {
  icon: React.ElementType;
  iconColor?: string;
  title: string;
  subtitle?: string;
  titleRight?: React.ReactNode;
  onClick?: () => void;
  loading?: boolean;
  className?: string;
  children: React.ReactNode;
  /**
   * Aktionen rechts im Kopf (z. B. „Kalender →"). Ersetzt den Chevron;
   * ohne `onClick` bleibt die Karte selbst nicht klickbar.
   */
  actions?: React.ReactNode;
  /** Abgesetzter Fußbereich mit Trennlinie (Summen, Sekundäraktion). */
  footer?: React.ReactNode;
  /** Innenabstand des Inhalts abschalten (z. B. für randlose Tabellen). */
  bodyClassName?: string;
}

const WidgetShell: React.FC<WidgetShellProps> = ({
  icon: Icon,
  iconColor = 'primary',
  title,
  subtitle,
  titleRight,
  onClick,
  loading = false,
  className = '',
  children,
  actions,
  footer,
  bodyClassName = '',
}) => {
  const colors = colorMap[iconColor] || colorMap.primary;

  if (loading) {
    return (
      <div className={`bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 min-h-[200px] flex flex-col ${className}`}>
        <div className="flex items-center justify-center flex-1">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 flex flex-col ${
        onClick ? 'cursor-pointer hover:shadow-md' : ''
      } transition-all duration-200 ${className}`}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-4">
        {/* min-w-0 auf beiden Ebenen, sonst schiebt ein langer Titel die Aktionen
            aus der Karte (sichtbar ab ~320 px Viewport-Breite). */}
        <div className="flex min-w-0 items-center gap-3">
          <div className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center ${colors.iconBg}`}>
            <Icon className={`h-5 w-5 ${colors.iconText}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <h3 className="break-words text-base font-bold text-gray-900 dark:text-white">
                {title}
              </h3>
              {titleRight}
            </div>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {actions ? (
          <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>
        ) : (
          onClick && (
            <ChevronRightIcon className="h-5 w-5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
          )
        )}
      </div>

      {/* Content */}
      <div className={`flex-1 overflow-hidden ${bodyClassName}`.trim()}>
        {children}
      </div>

      {footer && (
        <div className="-mx-4 mt-4 border-t border-gray-100 px-4 pt-3 dark:border-gray-700/70 sm:-mx-5 sm:px-5">
          {footer}
        </div>
      )}
    </div>
  );
};

export default WidgetShell;
